import { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UIList from 'UIComponents/list/UIList';
import UIButton from 'UIComponents/button/UIButton';
import UIRadioInput from 'UIComponents/input/UIRadioInput';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Small from 'UIComponents/elements/Small';
import ScopedFeatureTooltip from './permissions/ScopedFeatureTooltip';
import { FileVisibilityOptionNames, FileVisibilityOptions } from '../enums/FileVisibilityOptions';
import { RequestStatus } from '../Constants';
import ChangeFileVisibilityConfirmationModal from './modals/ChangeFileVisibilityConfirmationModal';
import FileVisibilityDisplayOptions from '../enums/FileVisibilityDisplayOptions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getI18nKey(suffix) {
  return `FileManagerCore.fileVisibility.${suffix}`;
}
const propTypes = {
  use: PropTypes.oneOf(Object.keys(FileVisibilityDisplayOptions)),
  showModal: PropTypes.bool,
  fileCount: PropTypes.number,
  isReadOnly: PropTypes.bool.isRequired,
  defaultVisibilityOption: PropTypes.oneOf(Object.keys(FileVisibilityOptions)),
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onVisibilitySelected: PropTypes.func,
  onCloseModal: PropTypes.func.isRequired,
  onRetryGetFileCount: PropTypes.func,
  changeVisibilityRequestStatus: PropTypes.oneOf(Object.keys(RequestStatus)).isRequired,
  fileCountRequestStatus: PropTypes.oneOf(Object.keys(RequestStatus)),
  isFromPickerChangeVisibility: PropTypes.bool.isRequired
};
class FileVisibility extends Component {
  constructor(props) {
    super(props);
    this.handleCloseConfirmationModal = () => {
      const {
        onCloseModal
      } = this.props;
      this.setState({
        isConfirmationModalOpen: false
      });
      onCloseModal();
    };
    this.handleVisibilityDropdownOptionClick = fileVisibilityOption => {
      const {
        onVisibilitySelected
      } = this.props;
      this.setState({
        selectedFileVisibilitySetting: fileVisibilityOption,
        isConfirmationModalOpen: true
      });
      onVisibilitySelected(fileVisibilityOption);
    };
    this.renderFileVisibilityDropdownOptions = () => {
      return Object.keys(FileVisibilityOptions).map(fileVisibilityOption => /*#__PURE__*/_jsxs(UIButton, {
        "data-test-id": fileVisibilityOption,
        onClick: () => this.handleVisibilityDropdownOptionClick(fileVisibilityOption),
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: getI18nKey(`${fileVisibilityOption}.text`)
        }), fileVisibilityOption === FileVisibilityOptionNames.HIDDEN_FROM_ALL && /*#__PURE__*/_jsx(UIHelpIcon, {
          className: "m-left-1",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: getI18nKey(`${fileVisibilityOption}.subtext`)
          }),
          tooltipPlacement: "top"
        })]
      }, fileVisibilityOption));
    };
    this.renderFileVisibilityDropdown = () => {
      const {
        isReadOnly
      } = this.props;
      const {
        selectedDropdownOption
      } = this.state;
      return /*#__PURE__*/_jsx(ScopedFeatureTooltip, {
        placement: "left",
        children: /*#__PURE__*/_jsx(UIDropdown, {
          "data-test-id": "public-accessibility-dropdown",
          readOnly: isReadOnly,
          buttonClassName: "p-x-0 p-y-1",
          buttonUse: "transparent",
          buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: getI18nKey(`${selectedDropdownOption}.text`)
          }),
          children: /*#__PURE__*/_jsx(UIList, {
            children: this.renderFileVisibilityDropdownOptions()
          })
        })
      });
    };
    this.renderFileVisibilityRadioButtons = () => {
      return Object.keys(FileVisibilityOptions).map(fileVisibilityOption => /*#__PURE__*/_jsx(UIRadioInput, {
        defaultChecked: false,
        className: "m-y-2",
        name: "bulk-file-visibility",
        description: /*#__PURE__*/_jsx(Small, {
          use: "help",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: getI18nKey(`${fileVisibilityOption}.help`)
          })
        }),
        onChange: () => {
          const {
            onVisibilitySelected
          } = this.props;
          this.setState({
            selectedFileVisibilitySetting: fileVisibilityOption
          });
          onVisibilitySelected(fileVisibilityOption);
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: getI18nKey(`${fileVisibilityOption}.text`)
        })
      }, fileVisibilityOption));
    };
    this.renderFileVisibilityOptions = () => {
      const {
        use
      } = this.props;
      switch (use) {
        case FileVisibilityDisplayOptions.RADIO:
          return this.renderFileVisibilityRadioButtons();
        default:
          return this.renderFileVisibilityDropdown();
      }
    };
    this.state = {
      isConfirmationModalOpen: false,
      selectedFileVisibilitySetting: props.defaultVisibilityOption,
      selectedDropdownOption: props.defaultVisibilityOption
    };
  }
  componentDidUpdate(prevProps) {
    const {
      defaultVisibilityOption
    } = this.props;
    if (defaultVisibilityOption !== prevProps.defaultVisibilityOption) {
      this.setState({
        selectedDropdownOption: defaultVisibilityOption
      });
    }
  }
  render() {
    const {
      selectedFileVisibilitySetting
    } = this.state;
    const {
      changeVisibilityRequestStatus,
      showModal,
      fileCount,
      onConfirm,
      onRetryGetFileCount,
      fileCountRequestStatus,
      onCancel,
      isFromPickerChangeVisibility
    } = this.props;
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [showModal && /*#__PURE__*/_jsx(ChangeFileVisibilityConfirmationModal, {
        totalNestedFileCount: fileCount,
        changeFileVisibilityRequestStatus: changeVisibilityRequestStatus,
        selectedFileVisibilitySetting: selectedFileVisibilitySetting,
        handleConfirm: onConfirm,
        handleClose: onCancel,
        fetchNestedCount: onRetryGetFileCount,
        totalNestedFileCountRequestStatus: fileCountRequestStatus,
        isFromPickerChangeVisibility: isFromPickerChangeVisibility
      }), this.renderFileVisibilityOptions()]
    });
  }
}
FileVisibility.defaultProps = {
  use: FileVisibilityDisplayOptions.DROPDOWN,
  showModal: false,
  isReadOnly: false,
  defaultVisibilityOption: undefined,
  onConfirm: () => {},
  onCancel: () => {},
  onVisibilitySelected: () => {},
  onCloseModal: () => {},
  onRetryGetFileCount: () => {},
  changeVisibilityRequestStatus: RequestStatus.UNINITIALIZED,
  fileCountRequestStatus: RequestStatus.UNINITIALIZED,
  isFromPickerChangeVisibility: false
};
export default FileVisibility;